/**
 * WorkspaceRealtimeService
 *
 * sl-jmyl.6: WorkspaceScope — one socket per workspace, always connected.
 * Socket.IO client for Presentation, Moderation, and Workspace Presence.
 *
 * SOLID:
 * - S: Single Responsibility — Workspace-level socket communication
 * - D: Dependency Inversion — Depends on abstractions (interfaces, LiveData)
 *
 * Architecture:
 * - Creates its own Socket.IO connection via createSocketConnection()
 * - Main namespace (SyncGateway handles all events)
 * - Socket connects lazily on first setActiveDoc() and stays alive
 * - Multiple docs can be active simultaneously (future split view)
 * - Exposes workspace-level Observables for Presence + Moderations
 */

import {
  AGENT_RUN_ENDED_EVENT,
  AgentRunEndedSchema,
} from '@streamlain/copilot-realtime-contract';
import { LiveData, Service } from '@toeverything/infra';
import type { Socket } from 'socket.io-client';

import {
  markBackendAvailable,
  onBackendAvailable,
} from '../../../utils/backend-availability';
import {
  createServerSocketConnection,
  ensureSocketServerConfig,
} from '../../../utils/socket';
import type { WorkspaceServerService } from '../../cloud';
import type { WorkspaceService } from '../../workspace';
import { PRESENCE_GRACE_PERIOD_MS } from '../types';

// =============================================================================
// Types (matching backend interfaces)
// =============================================================================

export type PresentationMode = 'free' | 'frames';

export interface ViewportState {
  zoom: number;
  centerX: number;
  centerY: number;
  visibleBounds?: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
}

export interface PresentationSettings {
  fillScreen: boolean;
  blackBackground: boolean;
  hideToolbar: boolean;
}

export interface ModerationInfo {
  sessionId: string;
  docId: string;
  moderatorClientId: string;
  moderatorUserId?: string;
  moderatorName?: string;
  mode: PresentationMode;
  isFullscreen: boolean;
  frameIndex?: number;
  frameId?: string;
  settings?: PresentationSettings;
  followerUserIds?: string[];
  reconnectingFollowerUserIds?: string[];
  followerCount: number;
}

// Keep PresenterInfo as alias for backward compat with types.ts
export type PresenterInfo = ModerationInfo;

export interface ModerationJoinPromptRequest {
  docId: string;
  sessionId: string;
  requestId: number;
}

export interface FollowFailedEvent {
  docId: string;
  error: string;
}

export interface SessionState {
  sessionId: string;
  docId: string;
  moderatorClientId: string;
  moderatorUserId?: string;
  moderatorName?: string;
  mode: PresentationMode;
  isFullscreen: boolean;
  frameIndex?: number;
  frameId?: string;
  viewport?: ViewportState;
  settings: PresentationSettings;
}

export type ModerationRecoveryState =
  | 'idle'
  | 'disconnected'
  | 'resuming'
  | 'expired';

type PresentationStoppedReason =
  | 'moderator_stopped'
  | 'moderator_disconnected'
  | 'timeout';

export interface PresenceDocEntry {
  docId: string;
  userIds: string[];
  users?: { id: string; name: string }[];
  /** Assistants of running chat turns, at work in this doc. */
  agents?: {
    sessionId: string;
    ownerId: string;
    name: string | null;
    activity: string | null;
  }[];
}

export interface WorkspaceMetaChangedEvent {
  workspaceId: string;
  changeType:
    | 'doc_created'
    | 'doc_deleted'
    | 'doc_trashed'
    | 'doc_restored'
    | 'doc_renamed'
    | 'doc_moved'
    | 'doc_visibility_changed'
    | 'folder_created'
    | 'folder_moved'
    | 'folder_trashed'
    | 'folder_restored'
    | 'folder_metadata_updated'
    | 'folder_visibility_changed'
    | 'folder_deleted'
    | 'area_created'
    | 'area_updated'
    | 'area_trashed'
    | 'tag_created'
    | 'tag_updated'
    | 'tag_deleted'
    | 'doc_tags_updated'
    | 'workspace_deleted';
  docId?: string | null;
  localMutationId?: string;
  timestamp: number;
  source: string;
}

// =============================================================================
// Socket Event Types
// =============================================================================

interface StartPayload {
  docId: string;
  workspaceId: string;
  mode: PresentationMode;
  settings: PresentationSettings;
  frameIndex?: number;
  frameId?: string;
}

interface UpdatePayload {
  docId: string;
  mode?: PresentationMode;
  frameIndex?: number;
  frameId?: string;
  viewport?: ViewportState;
  settings?: PresentationSettings;
  isFullscreen?: boolean;
  followerUserIds?: string[];
}

interface FollowPayload {
  docId: string;
  moderatorClientId: string;
}

export interface StartModerationResult {
  success: boolean;
  error?: string;
  sessionId?: string;
  moderatorClientId?: string;
  moderatorUserId?: string;
  moderatorName?: string;
  mode?: PresentationMode;
  isFullscreen?: boolean;
  frameIndex?: number;
  frameId?: string;
  settings?: PresentationSettings;
  followerUserIds?: string[];
  followerCount?: number;
}

// =============================================================================
// Service
// =============================================================================

const HEARTBEAT_INTERVAL_MS = 3000;
const ACTIVE_DOC_REFRESH_INTERVAL_MS = 15_000;

/**
 * Place of the person in an open document (tsk-wsshare-doc-editor-limit):
 * editor, reader beyond the editor places, or refused beyond all places.
 */
export type DocParticipantRole = 'editor' | 'reader' | 'refused';

interface DocRoleEvent {
  workspaceId: string;
  docId: string;
  role: DocParticipantRole;
}

export class WorkspaceRealtimeService extends Service {
  // ===========================================================================
  // Workspace-level Observables (accessible from sidebar / organize)
  // ===========================================================================

  /** sl-jmyl.6: Active moderation sessions in the workspace */
  readonly activeModerations$ = new LiveData<ModerationInfo[]>([]);

  /** sl-jmyl.6: Workspace presence — who is viewing which docs */
  readonly workspacePresence$ = new LiveData<PresenceDocEntry[]>([]);
  readonly presenceDiscoveryEnabled$ = new LiveData<boolean | null>(null);

  /** Workspace metadata invalidations; payload intentionally has no title. */
  readonly workspaceMetaChanged$ =
    new LiveData<WorkspaceMetaChangedEvent | null>(null);

  /** The id of the latest agent run of this workspace that ended. */
  readonly agentRunEnded$ = new LiveData<string | null>(null);

  // ===========================================================================
  // Doc-level Observables (for presentation StateMachine / followers)
  // ===========================================================================

  /** Current session state (when moderating or following) */
  readonly sessionState$ = new LiveData<SessionState | null>(null);

  /** Presenter's current viewport (for followers in free mode) */
  readonly presenterViewport$ = new LiveData<ViewportState | null>(null);

  /** Presenter's current frame (for followers in frame mode) */
  readonly presenterFrame$ = new LiveData<{
    frameIndex: number;
    frameId: string;
  } | null>(null);

  /** Whether currently moderating */
  readonly isModerating$ = new LiveData<boolean>(false);

  /** Whether currently following */
  readonly isFollowing$ = new LiveData<boolean>(false);

  /** New moderation notification (for current doc) */
  readonly newPresenter$ = new LiveData<ModerationInfo | null>(null);

  /** UI request to reopen the join prompt for an active moderation session. */
  readonly joinPromptRequest$ =
    new LiveData<ModerationJoinPromptRequest | null>(null);

  /** Moderation stopped notification */
  readonly presenterStopped$ = new LiveData<{
    docId: string;
    reason: PresentationStoppedReason;
  } | null>(null);

  /** Follow request failed before a confirmed session was established. */
  readonly followFailed$ = new LiveData<FollowFailedEvent | null>(null);

  /** Connection status */
  readonly isConnected$ = new LiveData<boolean>(false);

  /** Place of the person per open document, as the server answered. */
  readonly docRoles$ = new LiveData<ReadonlyMap<string, DocParticipantRole>>(
    new Map()
  );

  /** Explicit recovery state for moderation UI after socket loss/reconnect. */
  readonly moderationRecoveryState$ = new LiveData<ModerationRecoveryState>(
    'idle'
  );

  // ===========================================================================
  // Private State
  // ===========================================================================

  private socket: Socket | null = null;
  private heartbeatIntervalId?: ReturnType<typeof setInterval>;
  private activeDocRefreshIntervalId?: ReturnType<typeof setInterval>;
  private connecting: Promise<void> | null = null;
  private backendAvailabilityUnsubscribe: (() => void) | null = null;
  private recoveryConnectTimer?: ReturnType<typeof setTimeout>;
  private workspacePresenceRetainCount = 0;
  private moderationResumePending = false;
  private followerRejoinPending = false;
  private activeModerationsRefreshInFlight = false;
  private joinPromptRequestId = 0;
  private readonly reconnectingFollowerTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  /** All currently active docs (open editor tabs) */
  private readonly activeDocs = new Set<string>();

  /** Visible editor renderers retaining each active document. */
  private readonly activeDocRetainCounts = new Map<string, number>();

  /** The doc the user is currently focused on (last setActiveDoc call) */
  private currentDocId: string | null = null;

  getCurrentUserId(): string | null {
    return this.workspaceServerService.server?.account$.value?.id ?? null;
  }

  private hasRealtimeActivity(): boolean {
    return this.workspacePresenceRetainCount > 0 || this.activeDocs.size > 0;
  }

  retainWorkspacePresence(): () => void {
    this.workspacePresenceRetainCount += 1;
    void this.ensureConnected();

    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.workspacePresenceRetainCount = Math.max(
        0,
        this.workspacePresenceRetainCount - 1
      );
      this.stopActiveDocRefreshIfIdle();
    };
  }

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  // ===========================================================================
  // Connection (WorkspaceScope lifecycle)
  // ===========================================================================

  /**
   * Ensure socket is connected. Lazy: connects on first call, reuses after.
   * Called by setActiveDoc() and connect().
   */
  async ensureConnected(): Promise<void> {
    if (this.socket?.connected) return;
    if (this.connecting) return this.connecting;

    this.connecting = this.doConnect();
    try {
      await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  private async doConnect(): Promise<void> {
    this.startBackendAvailabilityListener();

    const server = this.workspaceServerService.server;
    if (!server) {
      console.warn('[RealtimeSocket] No server available');
      this.scheduleRecoveryConnect();
      return;
    }

    try {
      await ensureSocketServerConfig(server);
      if (this.socket && !this.socket.connected) {
        this.teardownSocket();
      }
      // sl-jmyl.9: Connect to /realtime namespace (not root / which is nbstore)
      this.socket = createServerSocketConnection(server, '/realtime', {
        auth: this.workspaceService.workspace.openOptions.externalDocAccess
          ? {
              externalDocAccess: true,
              workspaceId: this.workspaceService.workspace.id,
              docId:
                this.workspaceService.workspace.openOptions.externalDocAccess
                  .docId,
            }
          : undefined,
      });

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(
          () => reject(new Error('Connection timeout')),
          10000
        );
        this.socket!.once('connect', () => {
          clearTimeout(timeout);
          resolve();
        });
        this.socket!.once('connect_error', error => {
          clearTimeout(timeout);
          reject(error);
        });
      });

      this.setupEventListeners();
      markBackendAvailable();
      this.isConnected$.next(true);

      // Join workspace rooms (presentation + presence)
      await this.joinWorkspaceRooms();
      this.refreshActiveDocs();

      // Fetch active moderations
      await this.refreshActiveModerations();
    } catch (error) {
      console.error('[RealtimeSocket] Connection failed:', error);
      this.teardownSocket();
      this.scheduleRecoveryConnect();
    }
  }

  private startBackendAvailabilityListener(): void {
    if (this.backendAvailabilityUnsubscribe) return;

    this.backendAvailabilityUnsubscribe = onBackendAvailable(() => {
      this.scheduleRecoveryConnect();
    });
  }

  private scheduleRecoveryConnect(): void {
    if (!this.hasRealtimeActivity()) return;
    if (this.socket?.connected || this.recoveryConnectTimer) return;

    this.recoveryConnectTimer = setTimeout(() => {
      this.recoveryConnectTimer = undefined;
      if (this.socket?.connected || !this.hasRealtimeActivity()) {
        return;
      }
      void this.ensureConnected().then(() => {
        this.refreshActiveDocs();
        void this.refreshActiveModerations();
        this.startActiveDocRefresh();
      });
    }, 1000);
  }

  /**
   * sl-jmyl.9: Join workspace room on /realtime namespace.
   */
  private async joinWorkspaceRooms(): Promise<void> {
    if (!this.socket) return;

    const workspaceId = this.workspaceService.workspace.id;

    try {
      const result = await this.socket.emitWithAck('realtime:join-workspace', {
        workspaceId,
      });
      if (!result.success) {
        console.warn('[RealtimeSocket] Failed to join workspace room');
      }
    } catch (error) {
      console.warn('[RealtimeSocket] Failed to join workspace rooms:', error);
    }
  }

  private teardownSocket(): void {
    this.removeEventListeners();
    this.stopActiveDocRefresh();
    if (this.socket) {
      try {
        this.socket.disconnect();
      } catch {
        // Ignore
      }
      this.socket = null;
    }
    this.isConnected$.next(false);
  }

  // ===========================================================================
  // Active Doc Management (called by Editor)
  // ===========================================================================

  /**
   * Register a doc as actively viewed. Connects socket if needed.
   * Called by Editor.bindEditorContainer().
   *
   * sl-jmyl.11: Sends realtime:set-active-doc so backend tracks presence
   * based on actual viewing, not HotSync awareness joins.
   */
  async setActiveDoc(docId: string): Promise<void> {
    this.activeDocs.add(docId);
    this.currentDocId = docId;
    await this.ensureConnected();

    // The editor may have been disposed while the socket was connecting.
    if (!this.activeDocs.has(docId)) {
      return;
    }

    // Notify backend of active doc for presence tracking
    this.emitActiveDoc(docId);
    void this.refreshActiveModerations();
    this.startActiveDocRefresh();
  }

  /**
   * Retain presence while a visible editor renderer owns the document.
   *
   * Page/edgeless transitions can briefly mount the replacement renderer
   * before disposing the previous one. A plain Set cannot represent that
   * overlap: the old cleanup would clear presence for the live replacement.
   */
  retainActiveDoc(docId: string): () => void {
    const retainCount = this.activeDocRetainCounts.get(docId) ?? 0;
    this.activeDocRetainCounts.set(docId, retainCount + 1);
    if (retainCount === 0) {
      void this.setActiveDoc(docId).catch(error => {
        console.warn('[RealtimeSocket] Failed to retain active doc:', error);
      });
    }

    let released = false;
    return () => {
      if (released) return;
      released = true;

      const currentCount = this.activeDocRetainCounts.get(docId) ?? 0;
      if (currentCount > 1) {
        this.activeDocRetainCounts.set(docId, currentCount - 1);
        return;
      }

      this.activeDocRetainCounts.delete(docId);
      this.clearActiveDoc(docId);
    };
  }

  /**
   * Unregister a doc from active view.
   * Called by Editor cleanup. Does NOT disconnect the socket.
   *
   * sl-jmyl.11: Sends realtime:clear-active-doc for instant presence removal.
   */
  clearActiveDoc(docId: string): void {
    this.activeDocs.delete(docId);
    this.setDocRole(docId, null);

    // Notify backend to remove presence for this doc
    if (this.socket) {
      this.socket.emit('realtime:clear-active-doc', {
        workspaceId: this.workspaceService.workspace.id,
        docId,
      });
    }

    // If moderating this doc, stop moderation
    if (this.isModerating$.value && this.currentDocId === docId) {
      void this.stopModeration();
    }

    // Update currentDocId to another active doc, or null
    if (this.currentDocId === docId) {
      const remaining = this.activeDocs.values().next();
      this.currentDocId = remaining.done ? null : remaining.value;
    }

    this.stopActiveDocRefreshIfIdle();
  }

  private emitActiveDoc(docId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit(
      'realtime:set-active-doc',
      { workspaceId: this.workspaceService.workspace.id, docId },
      (ack?: { role?: DocParticipantRole }) => {
        if (ack?.role && this.activeDocs.has(docId))
          this.setDocRole(docId, ack.role);
      }
    );
  }

  private setDocRole(docId: string, role: DocParticipantRole | null): void {
    const roles = new Map(this.docRoles$.value);
    if (role) roles.set(docId, role);
    else roles.delete(docId);
    this.docRoles$.next(roles);
  }

  private readonly handleDocRole = (event: DocRoleEvent): void => {
    if (
      event.workspaceId !== this.workspaceService.workspace.id ||
      !this.activeDocs.has(event.docId)
    ) {
      return;
    }
    this.setDocRole(event.docId, event.role);
  };

  private refreshActiveDocs(): void {
    if (!this.socket?.connected || this.activeDocs.size === 0) {
      return;
    }
    for (const docId of this.activeDocs) {
      this.emitActiveDoc(docId);
    }
  }

  private startActiveDocRefresh(): void {
    if (this.activeDocRefreshIntervalId || !this.hasRealtimeActivity()) {
      return;
    }
    this.activeDocRefreshIntervalId = setInterval(() => {
      this.refreshActiveDocs();
      void this.refreshActiveModerations();
    }, ACTIVE_DOC_REFRESH_INTERVAL_MS);
  }

  private stopActiveDocRefreshIfIdle(): void {
    if (this.hasRealtimeActivity()) {
      return;
    }
    this.stopActiveDocRefresh();
  }

  private stopActiveDocRefresh(): void {
    if (this.activeDocRefreshIntervalId) {
      clearInterval(this.activeDocRefreshIntervalId);
      this.activeDocRefreshIntervalId = undefined;
    }
  }

  // ===========================================================================
  // Event Listeners
  // ===========================================================================

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('presentation:started', this.handleModerationStarted);
    this.socket.on('presentation:stopped', this.handleModerationStopped);
    this.socket.on('presentation:updated', this.handlePresentationUpdated);
    this.socket.on('presentation:follow-success', this.handleFollowSuccess);
    this.socket.on('presentation:follow-error', this.handleFollowError);
    this.socket.on('presentation:follower-joined', this.handleFollowerJoined);
    this.socket.on('presentation:follower-left', this.handleFollowerLeft);
    this.socket.on('workspace:presence', this.handleWorkspacePresence);
    this.socket.on('workspace:meta-changed', this.handleWorkspaceMetaChanged);
    this.socket.on('realtime:doc-role', this.handleDocRole);
    this.socket.on(AGENT_RUN_ENDED_EVENT, this.handleAgentRunEnded);
    this.socket.on('connect', this.handleSocketConnected);
    this.socket.on('connect_error', this.handleSocketConnectError);
    this.socket.on('disconnect', this.handleSocketDisconnected);
  }

  private removeEventListeners(): void {
    if (!this.socket) return;

    this.socket.off('presentation:started', this.handleModerationStarted);
    this.socket.off('presentation:stopped', this.handleModerationStopped);
    this.socket.off('presentation:updated', this.handlePresentationUpdated);
    this.socket.off('presentation:follow-success', this.handleFollowSuccess);
    this.socket.off('presentation:follow-error', this.handleFollowError);
    this.socket.off('presentation:follower-joined', this.handleFollowerJoined);
    this.socket.off('presentation:follower-left', this.handleFollowerLeft);
    this.socket.off('workspace:presence', this.handleWorkspacePresence);
    this.socket.off('workspace:meta-changed', this.handleWorkspaceMetaChanged);
    this.socket.off('realtime:doc-role', this.handleDocRole);
    this.socket.off(AGENT_RUN_ENDED_EVENT, this.handleAgentRunEnded);
    this.socket.off('connect', this.handleSocketConnected);
    this.socket.off('connect_error', this.handleSocketConnectError);
    this.socket.off('disconnect', this.handleSocketDisconnected);
  }

  private readonly handleSocketConnected = (): void => {
    markBackendAvailable();
    this.isConnected$.next(true);
    this.moderationRecoveryState$.next(
      this.moderationResumePending || this.followerRejoinPending
        ? 'resuming'
        : 'idle'
    );
    void this.joinWorkspaceRooms().then(() => {
      this.refreshActiveDocs();
    });
    void this.refreshActiveModerations();
  };

  private readonly handleSocketDisconnected = (): void => {
    this.isConnected$.next(false);
    this.moderationResumePending ||= this.isModerating$.value;
    this.followerRejoinPending ||= this.isFollowing$.value;
    this.moderationRecoveryState$.next(
      this.moderationResumePending || this.followerRejoinPending
        ? 'disconnected'
        : 'idle'
    );
    this.clearRemoteModerationUiState();
    this.scheduleRecoveryConnect();
  };

  private readonly handleSocketConnectError = (error: Error): void => {
    console.warn('[RealtimeSocket] Connect error:', error);
    this.isConnected$.next(false);
    this.scheduleRecoveryConnect();
  };

  private clearRemoteModerationUiState(): void {
    this.activeModerations$.next([]);
    this.workspacePresence$.next([]);
    this.presenceDiscoveryEnabled$.next(null);
    this.newPresenter$.next(null);
    this.joinPromptRequest$.next(null);
    this.presenterViewport$.next(null);
    this.presenterFrame$.next(null);
    this.clearAllFollowerReconnectTimers();
  }

  private readonly handleWorkspaceMetaChanged = (
    event: WorkspaceMetaChangedEvent
  ) => {
    if (event.workspaceId !== this.workspaceService.workspace.id) {
      return;
    }
    this.workspaceMetaChanged$.next(event);
  };

  private readonly handleAgentRunEnded = (payload: unknown) => {
    const ended = AgentRunEndedSchema.safeParse(payload);
    if (
      !ended.success ||
      ended.data.workspaceId !== this.workspaceService.workspace.id
    ) {
      return;
    }
    this.agentRunEnded$.next(ended.data.runId);
  };

  // ===========================================================================
  // Moderator Actions
  // ===========================================================================

  async startModeration(
    mode: PresentationMode,
    settings: PresentationSettings,
    frameIndex?: number,
    frameId?: string
  ): Promise<StartModerationResult> {
    if (!this.socket || !this.currentDocId) {
      return { success: false, error: 'Not connected' };
    }

    const payload: StartPayload = {
      docId: this.currentDocId,
      workspaceId: this.workspaceService.workspace.id,
      mode,
      settings,
      frameIndex,
      frameId,
    };

    try {
      const result = await this.socket.emitWithAck(
        'presentation:start',
        payload
      );

      if (result.success) {
        this.moderationRecoveryState$.next('idle');
        const sessionMode = result.mode ?? mode;
        const sessionSettings = result.settings ?? settings;
        const sessionFrameIndex = result.frameIndex ?? frameIndex;
        const sessionFrameId = result.frameId ?? frameId;
        const sessionIsFullscreen = result.isFullscreen ?? false;

        this.isModerating$.next(true);
        const current = this.activeModerations$.value;
        const existingModeration = current.find(
          m => m.docId === this.currentDocId
        );
        const followerUserIds =
          result.followerUserIds ?? existingModeration?.followerUserIds ?? [];
        const followerCount =
          result.followerCount ??
          Math.max(
            followerUserIds.length,
            existingModeration?.followerCount ?? 0
          );
        this.sessionState$.next({
          sessionId: result.sessionId!,
          docId: this.currentDocId,
          moderatorClientId: result.moderatorClientId ?? '',
          moderatorUserId: result.moderatorUserId,
          moderatorName: result.moderatorName,
          mode: sessionMode,
          isFullscreen: sessionIsFullscreen,
          frameIndex: sessionFrameIndex,
          frameId: sessionFrameId,
          settings: sessionSettings,
        });

        // Populate activeModerations$ locally (broadcast excludes sender)
        const moderation: ModerationInfo = {
          sessionId: result.sessionId!,
          docId: this.currentDocId,
          moderatorClientId: result.moderatorClientId ?? '',
          moderatorUserId: result.moderatorUserId,
          moderatorName: result.moderatorName,
          mode: sessionMode,
          isFullscreen: sessionIsFullscreen,
          frameIndex: sessionFrameIndex,
          frameId: sessionFrameId,
          settings: sessionSettings,
          followerUserIds,
          followerCount,
        };
        const currentWithoutDoc = current.filter(
          m => m.docId !== this.currentDocId
        );
        this.activeModerations$.next([
          ...currentWithoutDoc,
          this.withPreservedReconnectingFollowers(moderation),
        ]);

        this.startHeartbeat();
      }

      return result;
    } catch (error) {
      console.error('[RealtimeSocket] Failed to start moderation:', error);
      return { success: false, error: String(error) };
    }
  }

  async stopModeration(): Promise<boolean> {
    if (!this.socket || !this.currentDocId) return false;

    const docId = this.currentDocId;
    this.stopHeartbeat();

    try {
      const result = await this.socket.emitWithAck('presentation:stop', {
        docId,
      });

      this.isModerating$.next(false);
      this.sessionState$.next(null);
      this.moderationRecoveryState$.next('idle');

      const current = this.activeModerations$.value;
      this.activeModerations$.next(current.filter(m => m.docId !== docId));
      this.clearFollowerReconnectTimersForDoc(docId);

      return result?.success ?? true;
    } catch (error) {
      console.warn('[RealtimeSocket] Error stopping moderation:', error);
      this.isModerating$.next(false);
      this.sessionState$.next(null);
      this.moderationRecoveryState$.next('idle');
      const current = this.activeModerations$.value;
      this.activeModerations$.next(current.filter(m => m.docId !== docId));
      this.clearFollowerReconnectTimersForDoc(docId);
      return false;
    }
  }

  updatePresentation(update: Omit<UpdatePayload, 'docId'>): void {
    if (!this.socket || !this.currentDocId || !this.isModerating$.value) return;

    this.socket.emit('presentation:update', {
      docId: this.currentDocId,
      ...update,
    });

    const current = this.sessionState$.value;
    if (current) {
      this.sessionState$.next({
        ...current,
        ...update,
        settings: update.settings
          ? { ...current.settings, ...update.settings }
          : current.settings,
      });
    }
  }

  broadcastViewport(viewport: ViewportState): void {
    this.updatePresentation({ viewport });
  }

  setFrame(frameIndex: number, frameId: string): void {
    this.updatePresentation({ frameIndex, frameId });
  }

  setMode(mode: PresentationMode, frameIndex?: number, frameId?: string): void {
    this.updatePresentation({ mode, frameIndex, frameId });
  }

  setFullscreen(isFullscreen: boolean): void {
    this.updatePresentation({ isFullscreen });
  }

  updateSettings(settings: PresentationSettings): void {
    this.updatePresentation({ settings });
  }

  // ===========================================================================
  // Follower Actions
  // ===========================================================================

  async follow(moderatorClientId: string): Promise<void> {
    if (!this.socket || !this.currentDocId) return;

    const payload: FollowPayload = {
      docId: this.currentDocId,
      moderatorClientId,
    };

    this.socket.emit('presentation:follow', payload);
  }

  async unfollow(): Promise<void> {
    if (!this.socket || !this.currentDocId) return;

    try {
      await this.socket.emitWithAck('presentation:unfollow', {
        docId: this.currentDocId,
      });
    } catch (error) {
      console.warn('[RealtimeSocket] Error unfollowing:', error);
    }

    this.isFollowing$.next(false);
    this.sessionState$.next(null);
    this.presenterViewport$.next(null);
    this.presenterFrame$.next(null);
    this.moderationRecoveryState$.next('idle');
  }

  // ===========================================================================
  // UI Helpers
  // ===========================================================================

  dismissNewPresenter(): void {
    this.newPresenter$.next(null);
  }

  requestModerationJoinPrompt(docId: string, sessionId: string): void {
    this.joinPromptRequestId += 1;
    this.joinPromptRequest$.next({
      docId,
      sessionId,
      requestId: this.joinPromptRequestId,
    });
  }

  dismissPresenterStopped(): void {
    this.presenterStopped$.next(null);
  }

  // ===========================================================================
  // Query
  // ===========================================================================

  async refreshActiveModerations(): Promise<void> {
    if (!this.socket) return;
    if (this.activeModerationsRefreshInFlight) return;
    this.activeModerationsRefreshInFlight = true;

    try {
      const result = await this.socket.emitWithAck('presentation:get-active', {
        workspaceId: this.workspaceService.workspace.id,
      });

      const presenters = result.presenters ?? [];
      markBackendAvailable();
      if (this.moderationResumePending || this.followerRejoinPending) {
        this.moderationRecoveryState$.next('resuming');
      }
      this.activeModerations$.next(
        presenters.map((presenter: ModerationInfo) =>
          this.withPreservedReconnectingFollowers(presenter)
        )
      );
      await this.reconcileLocalSessionWithActiveModerations(presenters);
      if (this.moderationRecoveryState$.value === 'resuming') {
        this.moderationRecoveryState$.next('idle');
      }
    } catch (error) {
      console.warn('[RealtimeSocket] Failed to get active moderations:', error);
    } finally {
      this.activeModerationsRefreshInFlight = false;
    }
  }

  private async reconcileLocalSessionWithActiveModerations(
    moderations: ModerationInfo[]
  ): Promise<void> {
    const currentSession = this.sessionState$.value;
    if (!currentSession) return;

    const currentUserId = this.getCurrentUserId();
    const activeSession = moderations.find(
      moderation => moderation.sessionId === currentSession.sessionId
    );

    if (this.isModerating$.value) {
      const ownedSession =
        activeSession ??
        moderations.find(
          moderation =>
            moderation.docId === currentSession.docId &&
            moderation.moderatorUserId === currentUserId
        );

      if (!ownedSession) {
        this.expireLocalModerationSession();
        this.moderationResumePending = false;
        return;
      }

      if (this.moderationResumePending) {
        this.moderationResumePending = false;
        const resumed = await this.startModeration(
          ownedSession.mode,
          ownedSession.settings ?? currentSession.settings,
          ownedSession.frameIndex,
          ownedSession.frameId
        );
        if (!resumed.success) {
          this.expireLocalModerationSession();
        }
        return;
      }

      this.sessionState$.next({
        ...currentSession,
        sessionId: ownedSession.sessionId,
        docId: ownedSession.docId,
        moderatorClientId: ownedSession.moderatorClientId,
        moderatorUserId: ownedSession.moderatorUserId,
        moderatorName: ownedSession.moderatorName,
        mode: ownedSession.mode,
        isFullscreen: ownedSession.isFullscreen,
        frameIndex: ownedSession.frameIndex,
        frameId: ownedSession.frameId,
        settings: ownedSession.settings ?? currentSession.settings,
      });
      return;
    }

    if (this.isFollowing$.value && !activeSession) {
      this.followerRejoinPending = false;
      this.expireLocalFollowingSession();
      return;
    }

    if (this.isFollowing$.value && activeSession) {
      this.sessionState$.next({
        ...currentSession,
        sessionId: activeSession.sessionId,
        docId: activeSession.docId,
        moderatorClientId: activeSession.moderatorClientId,
        moderatorUserId: activeSession.moderatorUserId,
        moderatorName: activeSession.moderatorName,
        mode: activeSession.mode,
        isFullscreen: activeSession.isFullscreen,
        frameIndex: activeSession.frameIndex,
        frameId: activeSession.frameId,
        settings: activeSession.settings ?? currentSession.settings,
      });

      if (this.followerRejoinPending) {
        this.followerRejoinPending = false;
        await this.follow(activeSession.moderatorClientId);
      }
    }
  }

  private expireLocalModerationSession(): void {
    this.stopHeartbeat();
    this.isModerating$.next(false);
    this.sessionState$.next(null);
    this.presenterViewport$.next(null);
    this.presenterFrame$.next(null);
    this.newPresenter$.next(null);
    this.joinPromptRequest$.next(null);
    this.moderationRecoveryState$.next('expired');
  }

  private expireLocalFollowingSession(): void {
    this.isFollowing$.next(false);
    this.sessionState$.next(null);
    this.presenterViewport$.next(null);
    this.presenterFrame$.next(null);
    this.newPresenter$.next(null);
    this.joinPromptRequest$.next(null);
    this.moderationRecoveryState$.next('expired');
  }

  private withPreservedReconnectingFollowers(
    moderation: ModerationInfo
  ): ModerationInfo {
    const current = this.activeModerations$.value.find(
      existing => existing.docId === moderation.docId
    );
    const active = new Set(moderation.followerUserIds ?? []);
    const reconnecting = (current?.reconnectingFollowerUserIds ?? []).filter(
      userId => !active.has(userId)
    );
    return {
      ...moderation,
      followerUserIds: [...active],
      reconnectingFollowerUserIds: reconnecting,
    };
  }

  private followerReconnectKey(docId: string, userId: string): string {
    return `${docId}:${userId}`;
  }

  private clearFollowerReconnectTimer(docId: string, userId: string): void {
    const key = this.followerReconnectKey(docId, userId);
    const timer = this.reconnectingFollowerTimers.get(key);
    if (!timer) return;
    clearTimeout(timer);
    this.reconnectingFollowerTimers.delete(key);
  }

  private clearFollowerReconnectTimersForDoc(docId: string): void {
    for (const [key, timer] of this.reconnectingFollowerTimers) {
      if (!key.startsWith(`${docId}:`)) continue;
      clearTimeout(timer);
      this.reconnectingFollowerTimers.delete(key);
    }
  }

  private clearAllFollowerReconnectTimers(): void {
    for (const timer of this.reconnectingFollowerTimers.values()) {
      clearTimeout(timer);
    }
    this.reconnectingFollowerTimers.clear();
  }

  private markFollowerReconnecting(docId: string, userId: string): void {
    this.clearFollowerReconnectTimer(docId, userId);
    const key = this.followerReconnectKey(docId, userId);
    const timer = setTimeout(() => {
      this.reconnectingFollowerTimers.delete(key);
      const current = this.activeModerations$.value;
      const moderation = current.find(m => m.docId === docId);
      if (!moderation?.reconnectingFollowerUserIds?.includes(userId)) return;

      moderation.reconnectingFollowerUserIds =
        moderation.reconnectingFollowerUserIds.filter(id => id !== userId);
      this.activeModerations$.next([...current]);
    }, PRESENCE_GRACE_PERIOD_MS);
    this.reconnectingFollowerTimers.set(key, timer);
  }

  private reactivateFollower(docId: string, userId: string): void {
    this.clearFollowerReconnectTimer(docId, userId);
    const current = this.activeModerations$.value;
    const moderation = current.find(m => m.docId === docId);
    if (!moderation) return;

    moderation.followerUserIds = [
      ...new Set([...(moderation.followerUserIds ?? []), userId]),
    ];
    moderation.reconnectingFollowerUserIds = (
      moderation.reconnectingFollowerUserIds ?? []
    ).filter(id => id !== userId);
    moderation.followerCount = Math.max(
      moderation.followerCount,
      moderation.followerUserIds.length
    );
    this.activeModerations$.next([...current]);
  }

  /** @deprecated Use refreshActiveModerations() */
  async refreshActivePresenters(): Promise<void> {
    return this.refreshActiveModerations();
  }

  /** @deprecated Use activeModerations$ */
  get availablePresenters$(): LiveData<ModerationInfo[]> {
    return this.activeModerations$;
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  private readonly handleModerationStarted = (event: {
    sessionId: string;
    docId: string;
    moderatorClientId: string;
    moderatorUserId?: string;
    moderatorName?: string;
    mode: PresentationMode;
    isFullscreen: boolean;
    frameIndex?: number;
    frameId?: string;
    settings: PresentationSettings;
    followerUserIds?: string[];
    followerCount?: number;
  }): void => {
    markBackendAvailable();
    const current = this.activeModerations$.value;
    const existingModeration = current.find(m => m.docId === event.docId);
    const followerUserIds =
      event.followerUserIds ?? existingModeration?.followerUserIds ?? [];
    const followerCount =
      event.followerCount ??
      Math.max(followerUserIds.length, existingModeration?.followerCount ?? 0);
    const moderation: ModerationInfo = {
      sessionId: event.sessionId,
      docId: event.docId,
      moderatorClientId: event.moderatorClientId,
      moderatorUserId: event.moderatorUserId,
      moderatorName: event.moderatorName,
      mode: event.mode,
      isFullscreen: event.isFullscreen,
      frameIndex: event.frameIndex,
      frameId: event.frameId,
      settings: event.settings,
      followerUserIds,
      followerCount,
    };

    const preservedModeration =
      this.withPreservedReconnectingFollowers(moderation);
    const existing = current.findIndex(m => m.docId === event.docId);
    if (existing >= 0) {
      current[existing] = preservedModeration;
      this.activeModerations$.next([...current]);
    } else {
      this.activeModerations$.next([...current, preservedModeration]);
    }

    // Notify about new presenter (moderator never receives this — broadcast excludes sender)
    if (event.docId === this.currentDocId) {
      this.newPresenter$.next(preservedModeration);
    }
  };

  private readonly handleModerationStopped = (event: {
    docId: string;
    reason: PresentationStoppedReason;
  }): void => {
    const current = this.activeModerations$.value;
    this.activeModerations$.next(current.filter(m => m.docId !== event.docId));
    this.clearFollowerReconnectTimersForDoc(event.docId);

    if (this.sessionState$.value?.docId !== event.docId) return;

    this.moderationResumePending = false;
    this.followerRejoinPending = false;

    if (this.isModerating$.value) {
      if (event.reason === 'timeout') {
        this.expireLocalModerationSession();
      } else {
        this.stopHeartbeat();
        this.isModerating$.next(false);
        this.sessionState$.next(null);
        this.presenterViewport$.next(null);
        this.presenterFrame$.next(null);
        this.newPresenter$.next(null);
        this.joinPromptRequest$.next(null);
        this.moderationRecoveryState$.next('idle');
      }
      return;
    }

    if (this.isFollowing$.value) {
      if (event.reason === 'timeout') {
        this.expireLocalFollowingSession();
      } else {
        this.isFollowing$.next(false);
        this.sessionState$.next(null);
        this.presenterViewport$.next(null);
        this.presenterFrame$.next(null);
        this.newPresenter$.next(null);
        this.joinPromptRequest$.next(null);
        this.moderationRecoveryState$.next('idle');
      }

      this.presenterStopped$.next(event);
      setTimeout(() => this.presenterStopped$.next(null), 5000);
    }
  };

  private readonly handlePresentationUpdated = (event: {
    docId: string;
    mode?: PresentationMode;
    isFullscreen?: boolean;
    frameIndex?: number;
    frameId?: string;
    viewport?: ViewportState;
    settings?: PresentationSettings;
  }): void => {
    markBackendAvailable();
    // Update activeModerations$ (for all users)
    const currentModerations = this.activeModerations$.value;
    const moderation = currentModerations.find(m => m.docId === event.docId);
    let updatedModeration: ModerationInfo | null = null;
    let enteredFrameMode = false;
    if (moderation) {
      const previousMode = moderation.mode;
      let changed = false;
      if (event.mode !== undefined && moderation.mode !== event.mode) {
        moderation.mode = event.mode;
        changed = true;
      }
      if (
        event.isFullscreen !== undefined &&
        moderation.isFullscreen !== event.isFullscreen
      ) {
        moderation.isFullscreen = event.isFullscreen;
        changed = true;
      }
      if (
        event.frameIndex !== undefined &&
        moderation.frameIndex !== event.frameIndex
      ) {
        moderation.frameIndex = event.frameIndex;
        changed = true;
      }
      if (event.frameId !== undefined && moderation.frameId !== event.frameId) {
        moderation.frameId = event.frameId;
        changed = true;
      }
      if (event.settings !== undefined) {
        moderation.settings = {
          ...(moderation.settings ?? event.settings),
          ...event.settings,
        };
        changed = true;
      }
      if (changed) {
        this.activeModerations$.next([...currentModerations]);
      }
      updatedModeration = moderation;
      enteredFrameMode = event.mode === 'frames' && previousMode !== 'frames';
    }

    if (
      enteredFrameMode &&
      updatedModeration &&
      event.docId === this.currentDocId &&
      !this.isFollowing$.value &&
      !this.isModerating$.value
    ) {
      this.newPresenter$.next({
        ...updatedModeration,
        followerUserIds: [...(updatedModeration.followerUserIds ?? [])],
      });
      this.requestModerationJoinPrompt(
        event.docId,
        updatedModeration.sessionId
      );
    }

    // Only process session state updates if we're following this presenter
    if (
      !this.isFollowing$.value ||
      this.sessionState$.value?.docId !== event.docId
    ) {
      return;
    }

    const current = this.sessionState$.value;
    if (current) {
      this.sessionState$.next({
        ...current,
        mode: event.mode ?? current.mode,
        isFullscreen: event.isFullscreen ?? current.isFullscreen,
        frameIndex: event.frameIndex ?? current.frameIndex,
        frameId: event.frameId ?? current.frameId,
        viewport: event.viewport ?? current.viewport,
        settings: event.settings
          ? { ...current.settings, ...event.settings }
          : current.settings,
      });
    }

    if (event.viewport) {
      this.presenterViewport$.next(event.viewport);
    }

    if (event.frameIndex !== undefined && event.frameId !== undefined) {
      this.presenterFrame$.next({
        frameIndex: event.frameIndex,
        frameId: event.frameId,
      });
    }
  };

  private readonly handleFollowSuccess = (event: SessionState): void => {
    markBackendAvailable();
    this.isFollowing$.next(true);
    this.followFailed$.next(null);
    this.sessionState$.next(event);

    const currentUserId = this.getCurrentUserId();
    if (currentUserId) {
      this.reactivateFollower(event.docId, currentUserId);
    }
    this.moderationRecoveryState$.next('idle');

    if (event.viewport) {
      this.presenterViewport$.next(event.viewport);
    }

    if (event.frameIndex !== undefined && event.frameId !== undefined) {
      this.presenterFrame$.next({
        frameIndex: event.frameIndex,
        frameId: event.frameId,
      });
    }
  };

  private readonly handleFollowError = (event: FollowFailedEvent): void => {
    console.warn('[RealtimeSocket] Follow error:', event);
    this.followerRejoinPending = false;
    this.followFailed$.next(event);

    if (
      this.sessionState$.value?.docId === event.docId ||
      this.currentDocId === event.docId
    ) {
      this.expireLocalFollowingSession();
    }
  };

  private readonly handleFollowerJoined = (event: {
    docId: string;
    followerClientId: string;
    followerUserId?: string;
    followerName?: string;
    followerCount: number;
  }): void => {
    markBackendAvailable();
    const current = this.activeModerations$.value;
    const moderation = current.find(m => m.docId === event.docId);
    if (moderation) {
      if (event.followerUserId) {
        this.reactivateFollower(event.docId, event.followerUserId);
        const updated = this.activeModerations$.value;
        const updatedModeration = updated.find(m => m.docId === event.docId);
        if (updatedModeration) {
          updatedModeration.followerCount = event.followerCount;
          this.activeModerations$.next([...updated]);
        }
      } else {
        moderation.followerCount = event.followerCount;
        this.activeModerations$.next([...current]);
      }
    }
  };

  private readonly handleFollowerLeft = (event: {
    docId: string;
    followerClientId: string;
    followerUserId?: string;
    followerCount: number;
  }): void => {
    markBackendAvailable();
    const current = this.activeModerations$.value;
    const moderation = current.find(m => m.docId === event.docId);
    if (moderation) {
      if (event.followerUserId && moderation.followerUserIds) {
        moderation.followerUserIds = moderation.followerUserIds.filter(
          userId => userId !== event.followerUserId
        );
        moderation.reconnectingFollowerUserIds = [
          ...new Set([
            ...(moderation.reconnectingFollowerUserIds ?? []),
            event.followerUserId,
          ]),
        ];
        this.markFollowerReconnecting(event.docId, event.followerUserId);
      }
      moderation.followerCount = event.followerCount;
      this.activeModerations$.next([...current]);
    }
  };

  // ===========================================================================
  // Workspace Presence (Direct Socket — bypasses nbstore pipeline)
  // ===========================================================================

  private readonly handleWorkspacePresence = (event: {
    docs: PresenceDocEntry[];
    enabled?: boolean;
  }): void => {
    if (typeof event.enabled === 'boolean')
      this.presenceDiscoveryEnabled$.next(event.enabled);
    markBackendAvailable();
    this.workspacePresence$.next(event.docs);
  };

  // ===========================================================================
  // Heartbeat
  // ===========================================================================

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatIntervalId = setInterval(() => {
      if (this.socket && this.currentDocId && this.isModerating$.value) {
        this.socket.emit('presentation:heartbeat', {
          docId: this.currentDocId,
        });
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = undefined;
    }
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  override dispose(): void {
    this.stopHeartbeat();
    this.stopActiveDocRefresh();
    this.clearAllFollowerReconnectTimers();
    if (this.backendAvailabilityUnsubscribe) {
      this.backendAvailabilityUnsubscribe();
      this.backendAvailabilityUnsubscribe = null;
    }
    if (this.recoveryConnectTimer) {
      clearTimeout(this.recoveryConnectTimer);
      this.recoveryConnectTimer = undefined;
    }
    this.teardownSocket();
    super.dispose();
  }
}
